import {
  CreateIssueRequest,
  IssueSchema,
  NeedsIssueDto,
  UpdateIssueRequest,
  UpdateIssueSchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createNeedsMetadataSuccess,
  updateNeedsMetadataSuccess,
} from 'redux/slices/metaSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { useToast } from '../../../../app/ReusableToast';
import { AdminDrawerForm } from '../../AdminDrawer';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  isEdit: boolean;
  selectedIssue?: NeedsIssueDto;
}

export const NeedIssuesCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedIssue,
}: Props) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit ? UpdateIssueSchema : IssueSchema;
  const { control, handleSubmit, formState } = useForm<
    CreateIssueRequest | UpdateIssueRequest
  >({
    defaultValues: selectedIssue || {
      label: '',
      defaultTimeFrameInDays: 0,
      categoryId: 0,
      zcodeId: 0,
      tagCloud: '',
      key: '',
      activityIds: [],
    },
    resolver: zodResolver(schema),
  });
  const [needIssueResult, createNeedIssueLoading, createNeedIssue] = useApiCall(
    needRequests.createNeedsMetadata
  );
  const [updateNeedIssueResult, updateNeedIssueLoading, updateNeedIssue] =
    useApiCall(needRequests.updateNeedsMetadata);
  const needs = metaSliceSelectors.useSelectNeedsMetadata().data;
  const categoryOptions = needs.categories.map(c => ({
    label: c.label,
    value: c.id,
  }));
  const activityOptions = needs.activities.map(a => ({
    label: a.label,
    value: a.id,
  }));
  const zcodeOptions = needs.zcodes.map(z => ({
    label: z.key,
    value: z.id,
  }));
  const formConfig: FormConfig<CreateIssueRequest | UpdateIssueRequest> = [
    {
      type: 'input',
      name: 'label',
      label: 'Issue Label',
      input: 'text',
    },
    {
      type: 'input',
      name: 'defaultTimeFrameInDays',
      label: 'Default Time Frame in Days',
      input: 'number',
    },
    {
      type: 'input',
      name: 'categoryId',
      label: 'Category',
      input: 'select',
      props: {
        options: categoryOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'zcodeId',
      label: 'Zcode',
      input: 'select',
      props: {
        options: zcodeOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'tagCloud',
      label: 'Tag Cloud',
      input: 'text',
      props: {
        multiline: true,
      },
    },
    {
      type: 'input',
      name: 'key',
      label: 'Key',
      input: 'text',
    },
    {
      type: 'input',
      name: 'activityIds',
      label: 'Select Activities',
      input: 'autocomplete',
      props: {
        options: activityOptions,
        getOptionLabel: (option: any) => option.label,
        multiple: true,
      },
    },
  ];
  const onSubmit = async (data: CreateIssueRequest | UpdateIssueRequest) => {
    if (isEdit) {
      void updateNeedIssue({
        body: {
          ...data,
        },
        params: { type: 'issues', id: selectedIssue.id },
      });
    } else {
      void createNeedIssue({
        body: {
          ...(data as CreateIssueRequest),
        },
        params: { type: 'issues' },
      });
    }
  };
  // Handle successful creation
  useEffect(() => {
    if (needIssueResult && !createNeedIssueLoading && open) {
      dispatch(
        createNeedsMetadataSuccess({
          data: needIssueResult,
          type: 'issues',
        })
      );
      toast('Issue created successfully');
      setOpen(false);
    }
  }, [needIssueResult, createNeedIssueLoading]);

  // Handle successful update
  useEffect(() => {
    if (updateNeedIssueResult && !updateNeedIssueLoading && open) {
      dispatch(
        updateNeedsMetadataSuccess({
          data: updateNeedIssueResult,
          type: 'issues',
        })
      );
      toast('Issue updated successfully');
      setOpen(false);
    }
  }, [updateNeedIssueResult, updateNeedIssueLoading]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Issue` : `Add New Issue`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
